import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import Vditor from 'vditor';
import { ensureVditorRuntime } from './vditor-runtime';
import { normalizeMarkdownValue, shouldSyncVditorValue } from './vditor-value-sync';

export type VditorEditorTheme = 'auto' | 'light' | 'dark';

@Component({
  standalone: false,
  selector: 'app-vditor-editor',
  templateUrl: './vditor-editor.component.html',
  styleUrls: ['./vditor-editor.component.scss'],
})
export class VditorEditorComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() value: string | null | undefined = '';
  @Output() readonly valueChange = new EventEmitter<string>();
  @Input() readonly = false;
  @Input() disabled = false;
  @Input() placeholder = '';
  @Input() theme: VditorEditorTheme = 'auto';
  @Input() documentId = '';
  @Input() label = '';
  @Input() showLabel = false;
  @Input() toolbarPinned = false;
  @Input() showTimestampAction = false;
  @Output() readonly timestampRequested = new EventEmitter<void>();

  @ViewChild('vditorHost', { static: true }) private host!: ElementRef<HTMLDivElement>;

  initializationError = false;

  private editor?: Vditor;
  private viewReady = false;
  private editorReady = false;
  private destroyed = false;
  private suppressInput = false;
  private renderGeneration = 0;
  private rootThemeObserver?: MutationObserver;

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.observeApplicationTheme();
    this.recreateView();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.viewReady || this.destroyed) return;

    if (
      changes['documentId']
      || changes['readonly']
      || changes['disabled']
      || changes['placeholder']
      || changes['toolbarPinned']
      || changes['showTimestampAction']
    ) {
      this.recreateView();
      return;
    }

    if (changes['theme']) {
      this.observeApplicationTheme();
      this.applyTheme();
    }
    if (changes['value']) {
      this.syncExternalValue();
    }
  }

  /** Vditorの公開APIだけを使用して、現在のカーソル位置へプレーンテキストを挿入する。 */
  insertTextAtCursor(text: string): boolean {
    if (!this.editor || !this.editorReady || this.readonly || this.disabled) return false;
    this.editor.focus();
    const escapedText = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    this.editor.insertValue(escapedText);
    return true;
  }

  focus(): boolean {
    if (!this.editor || !this.editorReady || this.readonly || this.disabled) return false;
    this.editor.focus();
    return true;
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.renderGeneration++;
    this.rootThemeObserver?.disconnect();
    this.destroyEditor();
  }

  private recreateView(): void {
    if (!this.viewReady || this.destroyed) return;
    this.renderGeneration++;
    this.destroyEditor();
    this.host.nativeElement.replaceChildren();
    this.initializationError = false;

    if (this.readonly || this.disabled) {
      void this.renderReadonlyPreview(this.renderGeneration);
    } else {
      void this.createEditor(this.renderGeneration);
    }
  }

  private async createEditor(generation: number): Promise<void> {
    const initialValue = normalizeMarkdownValue(this.value);
    // 世代ごとにmount要素を分け、Lute読込中の旧インスタンスが新しいJOBのDOMを触らないようにする。
    const mountElement = document.createElement('div');
    this.host.nativeElement.replaceChildren(mountElement);
    this.suppressInput = true;
    this.editorReady = false;

    try {
      const i18n = await ensureVditorRuntime();
      if (this.destroyed || generation !== this.renderGeneration) return;

      const editor = new Vditor(mountElement, {
        mode: 'ir',
        lang: 'ja_JP',
        i18n,
        value: initialValue,
        placeholder: this.placeholder,
        minHeight: 210,
        cache: { enable: false },
        cdn: this.assetBaseUrl(),
        theme: this.resolvedTheme() === 'dark' ? 'dark' : 'classic',
        toolbar: this.toolbarItems(),
        toolbarConfig: { pin: this.toolbarPinned },
        link: { isOpen: false },
        image: { isPreview: false },
        upload: { url: '', linkToImgUrl: '', multiple: false },
        preview: {
          markdown: {
            sanitize: true,
            inlineMath: false,
            mathBlockPreview: false,
            codeBlockPreview: false,
          },
          hljs: { enable: false },
          render: { media: { enable: false } },
          theme: {
            current: '',
            path: '',
          },
        },
        after: () => {
          if (this.destroyed || generation !== this.renderGeneration || this.editor !== editor) {
            // VditorはLute読込後にUIを組み立てるため、初期化待ち中に破棄された場合はここでも除去する。
            editor.destroy();
            return;
          }
          this.editorReady = true;
          this.suppressInput = false;
          this.syncExternalValue();
        },
        input: value => {
          if (!this.editorReady || this.suppressInput || this.destroyed) return;
          if (value === normalizeMarkdownValue(this.value)) return;
          this.valueChange.emit(value);
        },
      });
      this.editor = editor;
    } catch (error) {
      console.error('Vditor initialization failed:', error);
      this.initializationError = true;
      this.suppressInput = false;
    }
  }

  private syncExternalValue(): void {
    if (this.readonly || this.disabled) {
      void this.renderReadonlyPreview(++this.renderGeneration);
      return;
    }
    if (!this.editor || !this.editorReady) return;

    const nextValue = normalizeMarkdownValue(this.value);
    if (!shouldSyncVditorValue(this.editor.getValue(), nextValue)) return;

    this.suppressInput = true;
    this.editor.setValue(nextValue, true);
    this.suppressInput = false;
  }

  private async renderReadonlyPreview(generation: number): Promise<void> {
    const element = this.host.nativeElement;
    const markdown = normalizeMarkdownValue(this.value);
    element.classList.remove('vditor-field__host--empty');
    if (!markdown) {
      element.replaceChildren();
      element.textContent = this.placeholder;
      element.classList.add('vditor-field__host--empty');
      return;
    }

    try {
      const i18n = await ensureVditorRuntime();
      if (this.destroyed || generation !== this.renderGeneration) return;
      // 非同期renderがJOB切替後の新しい表示を上書きしないよう、確定まではdetached要素で描画する。
      const previewElement = document.createElement('div');
      await Vditor.preview(previewElement, markdown, {
        cdn: this.assetBaseUrl(),
        lang: 'ja_JP',
        i18n,
        mode: this.resolvedTheme(),
        markdown: {
          sanitize: true,
          inlineMath: false,
          mathBlockPreview: false,
          codeBlockPreview: false,
        },
        hljs: { enable: false },
        render: { media: { enable: false } },
        theme: {
          current: '',
          path: '',
        },
      });
      if (this.destroyed || generation !== this.renderGeneration) return;
      element.replaceChildren(...Array.from(previewElement.childNodes));
      element.classList.add('vditor-reset');
      this.disablePreviewNavigation(element);
    } catch (error) {
      if (this.destroyed || generation !== this.renderGeneration) return;
      console.error('Vditor preview failed:', error);
      this.initializationError = true;
      element.textContent = markdown;
    }
  }

  private disablePreviewNavigation(element: HTMLElement): void {
    element.querySelectorAll('a').forEach(link => {
      link.addEventListener('click', event => event.preventDefault());
      link.setAttribute('rel', 'noopener noreferrer');
    });
  }

  private applyTheme(): void {
    if (this.editor && this.editorReady && !this.readonly && !this.disabled) {
      const dark = this.resolvedTheme() === 'dark';
      this.editor.setTheme(dark ? 'dark' : 'classic');
      return;
    }
    if (this.readonly || this.disabled) {
      void this.renderReadonlyPreview(++this.renderGeneration);
    }
  }

  private observeApplicationTheme(): void {
    this.rootThemeObserver?.disconnect();
    this.rootThemeObserver = undefined;
    if (this.theme !== 'auto') return;
    this.rootThemeObserver = new MutationObserver(() => this.applyTheme());
    this.rootThemeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
  }

  private resolvedTheme(): 'light' | 'dark' {
    if (this.theme === 'light' || this.theme === 'dark') return this.theme;
    return document.documentElement.classList.contains('ion-palette-dark') ? 'dark' : 'light';
  }

  private toolbarItems(): Array<string | IMenuItem> {
    const items: Array<string | IMenuItem> = [
      'headings', 'bold', 'italic', '|',
      'list', 'ordered-list', 'check', 'quote', '|',
      'code', 'inline-code', 'link', '|', 'undo', 'redo',
    ];
    if (!this.showTimestampAction) return items;

    items.push({
      name: 'timestamp-action',
      tip: '日時入力',
      tipPosition: 'sw',
      className: 'vditor-toolbar__item--timestamp',
      icon: '<svg class="vditor-toolbar-action__icon" viewBox="0 0 24 24" aria-hidden="true">'
        + '<path d="M12 2a10 10 0 1 0 10 10A10.01 10.01 0 0 0 12 2Zm0 18a8 8 0 1 1 8-8 8.01 8.01 0 0 1-8 8Zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67Z"></path>'
        + '</svg>',
      click: () => {
        if (!this.editorReady || this.readonly || this.disabled) return;
        this.timestampRequested.emit();
      },
    });
    return items;
  }

  private assetBaseUrl(): string {
    return new URL('assets/vditor', document.baseURI).toString().replace(/\/$/u, '');
  }

  private destroyEditor(): void {
    this.editorReady = false;
    this.suppressInput = false;
    this.editor?.destroy();
    this.editor = undefined;
  }
}
